import React, { useState, useEffect } from 'react';
import BoldText from '../components/BoldText';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import useDetailBreadcrumbs from '../hooks/useDetailBreadcrumbs';
import { useCapturePageContext } from '../capture/CaptureContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { getContact, updateContact, deleteContact, getAccounts, getLeadSources, getRepFirms, getUsers, optional } from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import InlineField from '../components/InlineField';
import KeyStar from '../components/KeyStar';
import { ArrowLeft, Trash2, Mail, Phone, Building2, Linkedin } from 'lucide-react';
import { AskAIButton } from '../components/ai';
import EmptyValue from '../components/EmptyValue';
import './AccountDetail/AccountDetail.css';

// A contact, every field editable in place. The update route takes the
// whole record, so a field saves with the rest of the contact around it.

const ROLES = [
  { value: '', label: 'Not set' }, { value: 'DM', label: 'DM — decision maker' }, { value: 'INF', label: 'INF — influences the decision' },
  { value: 'IM', label: 'IM — implementer' }, { value: 'CO', label: 'CO — coach' }, { value: 'NI', label: 'NI — no influence' },
];
const YES_NO = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];

function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { canDo, isModuleVisible } = useAuth();
  const [contact, setContact] = useState(null);
  useDetailBreadcrumbs(contact ? [
    ...(contact.account_id && contact.account_name
      ? [{ label: 'Accounts', path: '/accounts' }, { label: contact.account_name, path: `/accounts/${contact.account_id}?tab=contacts` }]
      : [{ label: 'Contacts', path: '/contacts' }]),
    { label: contact.name },
  ] : null);
  useCapturePageContext(contact && contact.account_id ? { account_id: contact.account_id, account_name: contact.account_name, contact_id: contact.id, contact_name: contact.name } : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [repFirms, setRepFirms] = useState([]);
  const [users, setUsers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [contactRes, accountsRes, sourcesRes, repFirmsRes, usersRes] = await Promise.all([
        getContact(id), optional(getAccounts()), optional(getLeadSources()), optional(getRepFirms()), optional(getUsers()),
      ]);
      setContact(contactRes.data);
      setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
      setLeadSources(Array.isArray(sourcesRes.data) ? sourcesRes.data : []);
      setRepFirms(Array.isArray(repFirmsRes.data) ? repFirmsRes.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (err) {
      setError('Failed to load contact');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = canDo('contacts', 'edit');

  // The record as the update route wants it, with one field changed.
  const saveField = async (field, value) => {
    const body = {
      first_name: contact.first_name || '', last_name: contact.last_name || '', account_id: contact.account_id || '',
      email: contact.email || '', phone: contact.phone || '', job_title: contact.job_title || '',
      city: contact.city || '', state: contact.state || '', country: contact.country || '', linkedin_url: contact.linkedin_url || '',
      lead_source_id: contact.lead_source_id || '', owner_id: contact.owner_id || '', rep_firm_id: contact.rep_firm_id || '',
      active: contact.active ?? true, market_to: contact.market_to ?? false, notes: contact.notes || '',
      influence_role: contact.influence_role || null,
      [field]: value,
    };
    body.name = `${body.first_name} ${body.last_name}`.trim();
    await updateContact(id, body);
    await loadData();
  };

  const handleDelete = async () => {
    try { await deleteContact(id); toast.success('Contact deleted'); navigate('/contacts'); }
    catch (err) { setError(err.response?.data?.error || 'Failed to delete contact'); }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;
  if (!contact) return <div className="alert alert-error">Contact not found</div>;

  const opts = (list, empty) => [{ value: '', label: empty }, ...(list || []).map((x) => ({ value: String(x.id), label: x.name }))];
  const nameOf = (value, list) => { const hit = (list || []).find((x) => String(x.id) === String(value)); return hit ? hit.name : ''; };

  return (
    <div className="contact-detail">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button className="btn btn-icon" onClick={() => navigate(location.state?.fromAccount ? `/accounts/${location.state.fromAccount}?tab=contacts` : '/contacts')} aria-label="Go back">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <InlineField value={contact.first_name || ''} onSave={(v) => saveField('first_name', v)} canEdit={canEdit} label="first name" emptyText="First name" />
              <InlineField value={contact.last_name || ''} onSave={(v) => saveField('last_name', v)} canEdit={canEdit} label="last name" emptyText="Last name" />
              <KeyStar on={!!contact.is_key_contact} name={contact.name || 'this contact'} size={20} onToggle={canEdit ? (on) => saveField('is_key_contact', on) : undefined} />
            </h1>
            <div className="flex gap-2 mt-2">
              {contact.active ? <span className="badge badge-success">Active</span> : <span className="badge badge-gray">Inactive</span>}
              {!!contact.market_to && <span className="badge badge-primary">Marketable</span>}
              {contact.influence_role && <span className="badge badge-gray">{contact.influence_role}</span>}
            </div>
          </div>
        </div>
        <div className="page-actions">
          {contact.account_id && <AskAIButton name={contact.name} />}
          {canDo('contacts', 'delete') && (
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)} aria-label="Delete contact">
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header">
          <h3 className="card-title">Contact Information</h3>
          {canEdit && <span className="text-muted text-sm">Click a value to change it</span>}
        </div>
        <div className="card-body">
          <div className="detail-row">
            <span className="detail-label">Email</span>
            <span className="detail-value"><InlineField value={contact.email || ''} onSave={(v) => saveField('email', v)} canEdit={canEdit} label="email" emptyText="Add email" format={(v) => <><Mail size={14} /> {v}</>} /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Phone</span>
            <span className="detail-value"><InlineField value={contact.phone || ''} onSave={(v) => saveField('phone', v)} canEdit={canEdit} label="phone" emptyText="Add phone" format={(v) => <><Phone size={14} /> {v}</>} /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Job Title</span>
            <span className="detail-value"><InlineField value={contact.job_title || ''} onSave={(v) => saveField('job_title', v)} canEdit={canEdit} label="job title" emptyText="Not set" /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Role in the decision</span>
            <span className="detail-value"><InlineField value={contact.influence_role || ''} onSave={(v) => saveField('influence_role', v || null)} canEdit={canEdit} type="select" options={ROLES} label="influence role" emptyText="Not set" /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">City</span>
            <span className="detail-value"><InlineField value={contact.city || ''} onSave={(v) => saveField('city', v)} canEdit={canEdit} label="city" emptyText="Not set" /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">State</span>
            <span className="detail-value"><InlineField value={contact.state || ''} onSave={(v) => saveField('state', v)} canEdit={canEdit} label="state" emptyText="Not set" /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Country</span>
            <span className="detail-value"><InlineField value={contact.country || ''} onSave={(v) => saveField('country', v)} canEdit={canEdit} label="country" emptyText="Not set" /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">LinkedIn</span>
            <span className="detail-value">
              <InlineField value={contact.linkedin_url || ''} onSave={(v) => saveField('linkedin_url', v)} canEdit={canEdit} label="LinkedIn profile" placeholder="https://linkedin.com/in/…" emptyText="Add LinkedIn" format={(v) => <a href={v} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}><Linkedin size={14} /> View Profile</a>} />
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Account</span>
            <span className="detail-value">
              {canEdit && accounts.length > 0 ? (
                <InlineField
                  value={contact.account_id ? String(contact.account_id) : ''}
                  onSave={(v) => saveField('account_id', v)}
                  canEdit
                  type="select"
                  options={accounts.map((a) => ({ value: String(a.id), label: a.name }))}
                  label="account"
                  format={(v) => (isModuleVisible('accounts') ? <Link to={`/accounts/${v}`} onClick={(e) => e.stopPropagation()}><Building2 size={14} /> {nameOf(v, accounts) || contact.account_name}</Link> : <><Building2 size={14} /> {nameOf(v, accounts) || contact.account_name}</>)}
                />
              ) : isModuleVisible('accounts') ? (
                <Link to={`/accounts/${contact.account_id}`}><Building2 size={14} /> {contact.account_name}</Link>
              ) : <span><Building2 size={14} /> {contact.account_name}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Lead Source</span>
            <span className="detail-value"><InlineField value={contact.lead_source_id ? String(contact.lead_source_id) : ''} onSave={(v) => saveField('lead_source_id', v)} canEdit={canEdit} type="select" options={opts(leadSources, 'None')} label="lead source" emptyText="Not set" /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Owner</span>
            <span className="detail-value"><InlineField value={contact.owner_id ? String(contact.owner_id) : ''} onSave={(v) => saveField('owner_id', v)} canEdit={canEdit} type="select" options={opts(users.filter((u) => u.active), 'Unassigned')} label="owner" emptyText="Unassigned" /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Rep Firm</span>
            <span className="detail-value">
              <InlineField value={contact.rep_firm_id ? String(contact.rep_firm_id) : ''} onSave={(v) => saveField('rep_firm_id', v)} canEdit={canEdit} type="select" options={opts(repFirms, 'None')} label="rep firm" emptyText="Not set" format={(v) => <Link to={`/rep-firms/${v}`} onClick={(e) => e.stopPropagation()}>{nameOf(v, repFirms) || '—'}</Link>} />
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Active</span>
            <span className="detail-value"><InlineField value={contact.active ? 'yes' : 'no'} onSave={(v) => saveField('active', v === 'yes')} canEdit={canEdit} type="select" options={YES_NO} label="active" /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Marketing</span>
            <span className="detail-value"><InlineField value={contact.market_to ? 'yes' : 'no'} onSave={(v) => saveField('market_to', v === 'yes')} canEdit={canEdit} type="select" options={YES_NO} label="marketing consent" format={(v) => (v === 'yes' ? 'Allowed' : 'Not allowed')} /></span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Notes</span>
            <span className="detail-value"><InlineField value={contact.notes || ''} onSave={(v) => saveField('notes', v)} canEdit={canEdit} multiline label="notes" emptyText="No notes" /></span>
          </div>
        </div>
      </div>

      {isModuleVisible('activities') && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Activities</h3>
          </div>
          <div className="card-body">
            {contact.activities?.length > 0 ? (
              <div className="activity-list-small">
                {contact.activities.map((a) => (
                  <div key={a.id} className="activity-item-small">
                    <div className="activity-item-header">
                      <span className="font-medium">{a.type}</span>
                      <span className="text-muted text-xs">{new Date(a.activity_date).toLocaleDateString()}</span>
                    </div>
                    {a.description && <p className="text-secondary text-sm"><BoldText text={a.description} /></p>}
                  </div>
                ))}
              </div>
            ) : <p className="text-secondary">No activities</p>}
          </div>
        </div>
      )}

      <ConfirmDialog isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={handleDelete} title="Delete Contact" message={`Delete ${contact.name}? This cannot be undone.`} confirmText="Delete" />
    </div>
  );
}

export default ContactDetail;
